import { useEffect, useMemo, useState } from "react";
import { IconAdjustments, IconArrowLeft, IconDownload, IconFilter } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Card, DatePickerInput, Input, NumberInput, Text } from "@/components/ui";
import { qk } from "@/lib/query-keys";
import { useConfirmDialog } from "@/lib/confirm-dialog-store";
import { toastErrorApi, toastExito } from "@/lib/toast";
import { formatIso, parseIso } from "@/features/datos-generales/utils/iso-date";
import {
  contarReporte,
  encolarExportacion,
  type RepDefinicion as RepDef,
} from "../services/reporteria.api";
import { useCatalogo } from "../hooks/use-reporteria";
import { usePreviewInfinito } from "../hooks/use-preview";
import { esEjecutable, hidratarEtiquetas } from "../state/builder-ops";
import * as ops from "../state/builder-ops";
import { PreviewTabla } from "./PreviewTabla";

const SIN_VALOR = new Set(["isnull", "isnotnull"]);

/**
 * Definición efectiva para EJECUTAR: descarta los filtros sin valor (filtros
 * opcionales). Así el reporte carga mostrando todo y el usuario acota al llenar
 * un filtro; un filtro a medias (between con un solo extremo) tampoco se aplica.
 */
function defParaEjecutar(def: RepDef): RepDef {
  const filtros = (def.filtros ?? []).filter((f) => {
    if (SIN_VALOR.has(f.operador)) return true;
    if (f.operador === "between") return !!(f.valor ?? "").trim() && !!(f.valor2 ?? "").trim();
    return !!(f.valor ?? "").trim();
  });
  return { ...def, filtros };
}

const ETIQUETA_OPERADOR: Record<string, string> = {
  "=": "igual a",
  "<>": "distinto de",
  ">": "mayor que",
  ">=": "mayor o igual",
  "<": "menor que",
  "<=": "menor o igual",
  contains: "contiene",
  startswith: "empieza con",
  notcontains: "no contiene",
  notstartswith: "no empieza con",
  between: "entre",
  isnull: "es vacío",
  isnotnull: "no es vacío",
};

/**
 * Vista de resultados de un reporte listo (Fase 1 · "reportes curados primero").
 * Muestra la TABLA del reporte + SOLO los filtros que el reporte ya trae, como
 * controles simples (fechas / número / texto), sin exponer columnas, joins ni
 * agregaciones. El usuario ajusta filtros, ve resultados en vivo y exporta a
 * Excel. "Personalizar" abre el builder libre (modo avanzado) con este reporte.
 */
export function SimpleReportView({
  initialDef,
  nombre,
  onPersonalizar,
  onVolver,
  puedeCrear = true,
}: {
  initialDef: RepDef;
  nombre: string;
  onPersonalizar: (def: RepDef) => void;
  onVolver: () => void;
  /** Si es false (rol Visor · solo lectura), se oculta "Personalizar" (que abre
   *  el builder para crear un reporte custom). Exportar a Excel SIEMPRE está
   *  disponible para quien tiene acceso al módulo de reportería. */
  puedeCrear?: boolean;
}) {
  const catalogo = useCatalogo();
  const qc = useQueryClient();
  const confirm = useConfirmDialog();
  const [def, setDefState] = useState<RepDef>(() => initialDef);
  const setDef = (f: (d: RepDef) => RepDef) => setDefState((prev) => f(prev));
  // Para ejecutar/exportar se usa la def con los filtros vacíos descartados
  // (memoizada para no re-crear la referencia en cada render → sin refetch extra).
  const defEjec = useMemo(() => defParaEjecutar(def), [def]);
  const activo = esEjecutable(defEjec);
  const datos = usePreviewInfinito(defEjec, activo);

  // Rellena etiquetas legibles del catálogo (los filtros guardan tokens opacos).
  useEffect(() => {
    if (catalogo.data) setDefState((d) => hidratarEtiquetas(d, catalogo.data!));
  }, [catalogo.data, def]);

  // token → { etiqueta, tipoDato } para rotular y tipar los controles de filtro.
  const metaPorToken = useMemo(() => {
    const m = new Map<string, { etiqueta: string; tipoDato: string | null }>();
    for (const t of catalogo.data?.tablas ?? [])
      for (const c of t.columnas) m.set(c.token, { etiqueta: c.etiqueta, tipoDato: c.tipoDato });
    return m;
  }, [catalogo.data]);

  const filtros = def.filtros ?? [];

  // Etiqueta del filtro: prioriza la del reporte (def.columnas · amigable y propia
  // de este reporte) sobre la del catálogo (que en columnas compartidas con otra
  // config puede venir como el nombre crudo), y cae al token solo si no hay ninguna.
  const etiquetaDeFiltro = (token: string) =>
    def.columnas.find((c) => c.token === token)?.etiqueta ||
    metaPorToken.get(token)?.etiqueta ||
    token;

  const exportar = useMutation({
    mutationFn: () => encolarExportacion(defEjec, nombre || "Reporte"),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.repTrabajos });
      toastExito({
        title: "Exportación en proceso",
        message: "La verás en “Mis reportes generados”.",
      });
    },
    onError: toastErrorApi,
  });

  async function exportarConConfirmacion() {
    let n: number | null = null;
    try {
      n = await contarReporte(defEjec);
    } catch {
      n = null;
    }
    const ok = await confirm({
      title: "Exportar a Excel",
      message:
        n != null
          ? `Se generará un archivo con ${n.toLocaleString("es-MX")} fila(s). ¿Continuar?`
          : "Se generará el archivo de Excel. ¿Continuar?",
      confirmLabel: "Exportar",
    });
    if (ok) exportar.mutate();
  }

  // Inalcanzable en la práctica: `ReporteriaBuilderPage` solo monta esta vista
  // con el catálogo ya cargado (misma query, caché compartida y observada por el
  // padre) y pinta su propio skeleton en la primera carga. Solo estrecha el tipo.
  if (!catalogo.data) return null;

  return (
    <div className="flex h-[calc(100dvh-4rem)] flex-col gap-3 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={onVolver}>
            Volver a reportes
          </Button>
          <Text fw={600} className="truncate">
            {nombre}
          </Text>
        </div>
        <div className="flex flex-wrap gap-2">
          {puedeCrear && (
            <Button
              variant="default"
              leftSection={<IconAdjustments size={16} />}
              onClick={() => onPersonalizar(def)}
            >
              Personalizar
            </Button>
          )}
          <Button
            leftSection={<IconDownload size={16} />}
            disabled={!activo}
            loading={exportar.isPending}
            onClick={exportarConConfirmacion}
          >
            Exportar a Excel
          </Button>
        </div>
      </div>

      {/* Barra de filtros · solo los que trae el reporte */}
      <Card className="p-3">
        <div className="mb-2 flex items-center gap-1.5">
          <IconFilter size={15} className="text-muted-foreground" />
          <Text fw={600} size="sm">
            Filtros
          </Text>
        </div>
        {filtros.length === 0 ? (
          <Text size="sm" c="dimmed">
            {puedeCrear
              ? "Este reporte no tiene filtros configurables. Si necesitas acotarlo, usa “Personalizar”."
              : "Este reporte no tiene filtros configurables."}
          </Text>
        ) : (
          <div className="flex flex-wrap gap-4">
            {filtros.map((f, i) => (
              <FiltroControl
                key={i}
                etiqueta={etiquetaDeFiltro(f.token)}
                tipoDato={metaPorToken.get(f.token)?.tipoDato ?? null}
                operador={f.operador}
                valor={f.valor ?? ""}
                valor2={f.valor2 ?? ""}
                onValor={(v) => setDef((d) => ops.updateFiltro(d, i, { valor: v }))}
                onValor2={(v) => setDef((d) => ops.updateFiltro(d, i, { valor2: v }))}
              />
            ))}
          </div>
        )}
      </Card>

      {/* Resultados · tabla no interactiva (sin exponer columnas/joins) */}
      <div className="min-h-0 flex-1">
        <PreviewTabla activo={activo} datos={datos} defEnCarga={def} />
      </div>
    </div>
  );
}

/** Control de un filtro individual, tipado por el tipo de dato de su columna. */
function FiltroControl({
  etiqueta,
  tipoDato,
  operador,
  valor,
  valor2,
  onValor,
  onValor2,
}: {
  etiqueta: string;
  tipoDato: string | null;
  operador: string;
  valor: string;
  valor2: string;
  onValor: (v: string) => void;
  onValor2: (v: string) => void;
}) {
  const tipo = (tipoDato ?? "").toLowerCase();
  const esFecha = /date|timestamp|fecha/.test(tipo);
  const esNumero = /int|numeric|decimal|real|double|money|float|serial/.test(tipo);
  const between = operador === "between";
  const sinValor = SIN_VALOR.has(operador);

  return (
    <div className="flex flex-col gap-1">
      <Text size="xs" c="dimmed">
        {etiqueta}
        <span className="ml-1 text-muted-foreground/70">· {ETIQUETA_OPERADOR[operador] ?? operador}</span>
      </Text>
      {sinValor ? (
        <Text size="sm" className="text-muted-foreground">
          (sin valor)
        </Text>
      ) : (
        <div className="flex items-center gap-1.5">
          <CampoValor tipoFecha={esFecha} tipoNumero={esNumero} valor={valor} onChange={onValor} />
          {between && (
            <>
              <Text size="sm" c="dimmed">
                y
              </Text>
              <CampoValor tipoFecha={esFecha} tipoNumero={esNumero} valor={valor2} onChange={onValor2} />
            </>
          )}
        </div>
      )}
    </div>
  );
}

function CampoValor({
  tipoFecha,
  tipoNumero,
  valor,
  onChange,
}: {
  tipoFecha: boolean;
  tipoNumero: boolean;
  valor: string;
  onChange: (v: string) => void;
}) {
  if (tipoFecha) {
    return (
      <DatePickerInput
        size="sm"
        clearable
        wrapperClassName="w-40"
        value={parseIso(valor)}
        onChange={(d) => onChange(formatIso(d))}
        placeholder="Fecha"
      />
    );
  }
  if (tipoNumero) {
    return (
      <div className="w-32">
        <NumberInput
          size="sm"
          value={valor === "" ? "" : Number(valor)}
          onChange={(v) => onChange(v === "" ? "" : String(v))}
          placeholder="Valor"
        />
      </div>
    );
  }
  return (
    <Input
      size="sm"
      className="w-40"
      value={valor}
      onChange={(e) => onChange(e.currentTarget.value)}
      placeholder="Valor"
    />
  );
}
